import { useCallback, useMemo, useState, useSyncExternalStore } from "react";
import type { EventId, PlanEvent } from "../../../../shared/types";
import { CATEGORY_BAR_COLORS } from "../../../../shared/types";
import type { UpdateEventData } from "../../types/event-form-types";
import { EventItemExpanded } from "./EventItemExpanded";
import { useEventEditing } from "../../hooks/use-event-editing";
import { EventItemHeader } from "./EventItemHeader";
import { ContextMenu } from "../../../../ui/atoms/ContextMenu";
import { ConfirmDialog } from "../../../../ui/atoms/ConfirmDialog";
import { RecurrenceScopeDialog } from "../../../calendar/components/dialogs/RecurrenceScopeDialog";
import * as taskStore from "../../../tasks/store/task-store";

interface EventItemProps {
  readonly event: PlanEvent;
  readonly defaultExpanded?: boolean;
  readonly onDelete?: (id: EventId, expandedEvent?: PlanEvent, skipRecurrenceCheck?: boolean) => void;
  readonly onUpdate?: (id: EventId, updates: UpdateEventData) => void;
  readonly onAddRecurrenceException?: (baseId: EventId, date: string) => void;
  readonly readOnly?: boolean;
}


export function EventItem({
  event,
  defaultExpanded = false,
  onDelete,
  onUpdate,
  onAddRecurrenceException,
  readOnly,
}: EventItemProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [contextMenu, setContextMenu] = useState<DOMRect | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [recurrenceDeleteOpen, setRecurrenceDeleteOpen] = useState(false);
  const noopUpdate = useCallback(() => {}, []);
  const { editingField, editValue, startEdit, saveEdit, setEditValue } =
    useEventEditing(event, onUpdate ?? noopUpdate);

  const allTasks = useSyncExternalStore(
    taskStore.subscribe,
    taskStore.getSnapshot,
  );

  const linkedTasks = useMemo(
    () => allTasks.filter((t) => t.eventId === event.id && !t.archived),
    [allTasks, event.id],
  );
  const linkedTaskCount = linkedTasks.length;
  const linkedDoneCount = linkedTasks.filter((t) => t.status === "done").length;

  const isRecurring = !!event.recurrence?.type;

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = new DOMRect(e.clientX, e.clientY, 0, 0);
    setContextMenu(rect);
  }, []);

  const handleDeleteClick = useCallback(() => {
    if (isRecurring && onAddRecurrenceException) {
      setRecurrenceDeleteOpen(true);
    } else {
      setConfirmDelete(true);
    }
  }, [isRecurring, onAddRecurrenceException]);

  const handleConfirmDelete = useCallback(() => {
    setConfirmDelete(false);
    onDelete?.(event.id);
  }, [onDelete, event.id]);

  const getBaseId = useCallback(() => {
    const suffix = `_${event.date}`;
    return (event.id.endsWith(suffix)
      ? event.id.slice(0, -suffix.length)
      : event.id) as EventId;
  }, [event.id, event.date]);

  const handleRecurrenceDeleteThis = useCallback(() => {
    setRecurrenceDeleteOpen(false);
    const baseId = getBaseId();
    onAddRecurrenceException?.(baseId, event.date);
  }, [getBaseId, event.date, onAddRecurrenceException]);

  const handleRecurrenceDeleteAll = useCallback(() => {
    setRecurrenceDeleteOpen(false);
    const baseId = getBaseId();
    onDelete?.(baseId, undefined, true);
  }, [getBaseId, onDelete]);

  return (
    <div
      className="bg-surface border border-border rounded-xl overflow-hidden relative"
      onContextMenu={handleContextMenu}
    >
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 ${CATEGORY_BAR_COLORS[event.category]}`}
      />
      <EventItemHeader
        event={event}
        isExpanded={isExpanded}
        linkedTaskCount={linkedTaskCount}
        linkedDoneCount={linkedDoneCount}
        editingField={readOnly ? null : editingField}
        editValue={editValue}
        onToggleExpanded={() => setIsExpanded(!isExpanded)}
        onStartEdit={readOnly ? undefined : startEdit}
        onSaveEdit={readOnly ? undefined : saveEdit}
        onEditValueChange={readOnly ? undefined : setEditValue}
        onDelete={readOnly ? undefined : handleDeleteClick}
      />

      {isExpanded && (
        <EventItemExpanded
          event={event}
          linkedTasks={linkedTasks}
          editingField={readOnly ? null : editingField}
          editValue={editValue}
          onStartEdit={readOnly ? undefined : startEdit}
          onSaveEdit={readOnly ? undefined : saveEdit}
          onEditValueChange={readOnly ? undefined : setEditValue}
          onUpdate={readOnly ? undefined : onUpdate}
          readOnly={readOnly}
        />
      )}

      {contextMenu && !readOnly && (
        <ContextMenu
          items={[
            {
              label: "Modifier",
              onClick: () => {
                setIsExpanded(true);
                startEdit("title", event.title);
              },
            },
            {
              label: "Supprimer",
              onClick: handleDeleteClick,
              danger: true,
            },
          ]}
          anchorRect={contextMenu}
          onClose={() => setContextMenu(null)}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Supprimer l'événement"
        message={`Supprimer « ${event.title} » ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <RecurrenceScopeDialog
        open={recurrenceDeleteOpen}
        mode="delete"
        onThis={handleRecurrenceDeleteThis}
        onAll={handleRecurrenceDeleteAll}
        onCancel={() => setRecurrenceDeleteOpen(false)}
      />
    </div>
  );
}
